import { Injectable, NgZone, effect, inject } from '@angular/core';
import { Router } from '@angular/router';

// Services
import { AuthService } from './auth.service';
import { ToastService } from '../../shared/services/toast.service';


@Injectable({ providedIn: 'root' })
export class InactivityService {

  private readonly INACTIVITY_LIMIT = 30 * 60 * 1000;  // 30 min
  private readonly WARNING_TIME = 5 * 60 * 1000;       // last 5 min
  private readonly THROTTLE_MS = 10000;                // 10s throttle on events
  private readonly CHECK_INTERVAL = 15000;             // 15s polling anti-throttle

  private lastActivity = Date.now();
  private listeners: (() => void)[] = [];
  private intervalId?: number;

  private hasShownWarning = false;

  private readonly zone = inject(NgZone);
  private readonly authSrv = inject(AuthService);
  private readonly toastSrv = inject(ToastService);
  private readonly router = inject(Router);

  constructor() {
    effect(() => {
      const loggedIn = this.authSrv.isLoggedIn()();

      if (loggedIn) {
        this.zone.runOutsideAngular(() => this.startTracking());
      } else {
        this.stopTracking();
      }
    });
  }

  /** Avvia il tracking */
  private startTracking() {
    this.stopTracking(); // sicurezza
    this.hasShownWarning = false;
    this.lastActivity = Date.now();

    // Listener attività
    const handler = () => {
      const now = Date.now();
      if (now - this.lastActivity > this.THROTTLE_MS) {
        this.lastActivity = now;
        this.hasShownWarning = false;
      }
    };

    ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(event => {
      const listener = () => handler();
      document.addEventListener(event, listener, { passive: true });
      this.listeners.push(() => document.removeEventListener(event, listener));
    });

    // Anti-throttling timer
    // Non serve runInsideAngular → meno change detection
    this.intervalId = window.setInterval(() => this.checkInactivity(), this.CHECK_INTERVAL);
  }

  /** Stop tracking */
  private stopTracking() {
    this.listeners.forEach(off => off());
    this.listeners = [];

    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = undefined;

    this.hasShownWarning = false;
  }

  /** Controllo inattività basato su tempo REALE */
  private checkInactivity() {
    const now = Date.now();
    const diff = now - this.lastActivity;

    // Mostra warning solo una volta
    if (!this.hasShownWarning && diff >= this.INACTIVITY_LIMIT - this.WARNING_TIME) {
      this.hasShownWarning = true;

      this.zone.run(() => {
        this.toastSrv.show({
          type: 'warning',
          message: 'La sessione scadrà tra 5 minuti per inattività.'
        });
        console.warn('Inactivity warning: session will expire in 5 minutes.');
      });
    }

    // Tempo scaduto
    if (diff >= this.INACTIVITY_LIMIT) {
      this.zone.run(() => {
        this.stopTracking();
        this.router.navigate(['/auth/login']);
        this.toastSrv.show({
          type: 'error',
          message: 'Sessione scaduta per inattività.',
          autoHide: false
        });
        console.warn('Session expired due to inactivity.');
      });
    }
  }

}
